import React, { lazy } from 'react';
import ProtectedRoute from './ProtectedRoute';
import routeCodes from 'common/constants/route-codes';

import { StyledToastContainer } from 'components/organisms/Toast';
import SuperAdminAppShell from 'components/layouts/SuperAdmin/AppShell';
import { Switch } from 'react-router-dom';

const Home = lazy(() => import('pages/Home'));
const StatisticsPage = lazy(() => import('pages/Orders'));
const NotificationsPage = lazy(() => import('pages/super-admin-pages/notifications'));
const NotificationsTicketsPage = lazy(() => import('pages/super-admin-pages/notifications/tickets'));

const RestaurantsList = lazy(() => import('pages/restaurant/list'));
const RestaurantsShow = lazy(() => import('pages/restaurant/show'));
const RestaurantsCreate = lazy(() => import('pages/restaurant/create'));

const ProfileList = lazy(() => import('pages/profile/list'));
const ProfileShow = lazy(() => import('pages/profile/show'));

const FirmList = lazy(() => import('pages/firm/list'));
const FirmShow = lazy(() => import('pages/firm/show'));
const FormCreate = lazy(() => import('pages/firm/create'));

const TeamPage = lazy(() => import('pages/Team'));
const TeamCreatePage = lazy(() => import('pages/Team/create'));
const TeamEditPage = lazy(() => import('pages/Team/edit'));

const MenuPage = lazy(() => import('pages/menu'));
const MenuOptionsCreate = lazy(() => import('pages/menu/options/create'));
const MenuOptionsEdit = lazy(() => import('pages/menu/options/edit'));

const StripeRefresh = lazy(() => import('pages/Stripe/refresh'));
const StripeSuccess = lazy(() => import('pages/Stripe/success'));

const MenuCategories = lazy(() => import('pages/menu/categories'));
const MenuCategoriesCreate = lazy(() => import('pages/menu/categories/create'));
const MenuCategoriesEdit = lazy(() => import('pages/menu/categories/edit'));

const MenuDishesCreate = lazy(() => import('pages/menu/dishes/create'));
const MenuDishesEdit = lazy(() => import('pages/menu/dishes/edit'));

const MenuSpecialMenuCreate = lazy(() =>
  import('pages/menu/special_menu/create'),
);
const MenuSpecialMenuEdit = lazy(() => import('pages/menu/special_menu/edit'));

const SettingsV2Page = lazy(() => import('pages/SettingsV2'));
const SettingsV2IpraticoPage = lazy(() => import('pages/SettingsV2/ipratico'));
const SettingsV2Delivery = lazy(() => import('pages/SettingsV2/delivery'));
const SettingsV2ServiceCreate = lazy(() =>
  import('pages/SettingsV2/service-create'),
);
const SettingsV2ServiceEdit = lazy(() =>
  import('pages/SettingsV2/service-edit'),
);

const QRMainPage = lazy(() => import('pages/QR'));

const OffersMainPage = lazy(() => import('pages/Offers'));
const OffersCreateCalendars = lazy(() =>
  import('pages/Offers/create/bonus_calendars'),
);
const OffersCreatePayment = lazy(() =>
  import('pages/Offers/create/bonuse_payment'),
);
const OffersCreateAmount = lazy(() =>
  import('pages/Offers/create/bonus_amount'),
);
const OffersCreateBonusReservation = lazy(() =>
  import('pages/Offers/create/bonus_reservations'),
);
const ManualBonusesPage = lazy(() => import('pages/super-admin-pages/bonuses/manual-bonuses'));

const SuperAdminHome = lazy(() => import('pages/super-admin-pages/home'));
const SuperAdminUserStatistics = lazy(() => import('pages/super-admin-pages/users-statistic'));

const SuperAdminRestaurants = lazy(() =>
  import('pages/super-admin-pages/restaurants'),
);
const SuperAdminBonusesRestaurants = lazy(() =>
  import('pages/super-admin-pages/pay-bonuses-restaurants'),
);
const SuperAdminRestaurantsShow = lazy(() =>
  import('pages/super-admin-pages/restaurants/show'),
);

const HelpPage = lazy(() => import('pages/Help'));
const Ipratico = lazy(() => import('pages/ipratico'));

const Administration = lazy(() => import('pages/super-admin-pages/administrations'));

const AdministrationOwners = lazy(() => import('pages/super-admin-pages/administrations/owners/list'));
const AdministrationOwnersCreate = lazy(() => import('pages/super-admin-pages/administrations/owners/create'));
const AdministrationOwnersShow = lazy(() => import('pages/super-admin-pages/administrations/owners/show'));

const AdministrationCompanies = lazy(() => import('pages/super-admin-pages/administrations/company/list'));
const AdministrationCompaniesCreate = lazy(() => import('pages/super-admin-pages/administrations/company/create'));
const AdministrationCompaniesShow = lazy(() => import('pages/super-admin-pages/administrations/company/show'));

const AdministrationRestaurants = lazy(() => import('pages/super-admin-pages/administrations/restaurant/list'));
const AdministrationRestaurantsCreate = lazy(() => import('pages/super-admin-pages/administrations/restaurant/create'));
const AdministrationRestaurantsShow = lazy(() => import('pages/super-admin-pages/administrations/restaurant/show'));

const BonusesPage = lazy(() => import('pages/super-admin-pages/bonuses'));
const InviteFriendPage = lazy(() => import('pages/super-admin-pages/bonuses/invite-friend/create'));
const EveryBookingPage = lazy(() => import('pages/super-admin-pages/bonuses/every-booking/create'));
const FromOwnersToUserPage = lazy(() => import('pages/super-admin-pages/bonuses/from-owners-to-user/create'));
const DiscoverRestaurantPage = lazy(() => import('pages/super-admin-pages/bonuses/discover-restaurant/create'));
const SuperBonusPage = lazy(() => import('pages/super-admin-pages/bonuses/super-bonus/create'));

const SuperAdminRoutes = () => {
  return (
    <>
      <StyledToastContainer />
      <SuperAdminAppShell>
        <Switch>
          <ProtectedRoute component={RestaurantsList} exact path={routeCodes.restaurant} />
          <ProtectedRoute component={RestaurantsCreate} exact path={routeCodes.restaurantCreate} />
          <ProtectedRoute component={RestaurantsShow} exact path={routeCodes.restaurantView} />
          <ProtectedRoute component={NotificationsPage} exact path={routeCodes.superAdmin.notifications} />
          <ProtectedRoute component={NotificationsTicketsPage} exact path={routeCodes.superAdmin.notificationsTickets} />

          <ProtectedRoute component={BonusesPage} exact path={routeCodes.bonuses} />
          <ProtectedRoute component={InviteFriendPage} exact path={routeCodes.bonusesInvite} />
          <ProtectedRoute component={InviteFriendPage} exact path={routeCodes.bonusesInviteEdit} />
          <ProtectedRoute component={EveryBookingPage} exact path={routeCodes.bonusesEveryBooking} />
          <ProtectedRoute component={EveryBookingPage} exact path={routeCodes.bonusesEveryBookingEdit} />
          <ProtectedRoute component={FromOwnersToUserPage} exact path={routeCodes.bonusFromOwnersToUser} />
          <ProtectedRoute component={FromOwnersToUserPage} exact path={routeCodes.bonusFromOwnersToUserEdit} />
          <ProtectedRoute component={SuperBonusPage} exact path={routeCodes.bonusSuperBonus} />
          <ProtectedRoute component={SuperBonusPage} exact path={routeCodes.bonusSuperBonusEdit} />
          <ProtectedRoute component={DiscoverRestaurantPage} exact path={routeCodes.bonusDiscoverRestaurant} />
          <ProtectedRoute component={DiscoverRestaurantPage} exact path={routeCodes.bonusDiscoverRestaurantEdit} />
          <ProtectedRoute component={ManualBonusesPage} exact path={routeCodes.bonusManual} />
          <ProtectedRoute component={ManualBonusesPage} exact path={routeCodes.bonusManualEdit} />


          <ProtectedRoute component={ProfileList} exact path={routeCodes.profile} />
          <ProtectedRoute component={ProfileShow} exact path={routeCodes.profileEdit} />

          <ProtectedRoute component={FirmList} exact path={routeCodes.firm} />
          <ProtectedRoute component={FormCreate} exact path={routeCodes.firmCreate} />
          <ProtectedRoute component={FirmShow} exact path={routeCodes.firmView} />

          <ProtectedRoute component={Administration} exact path={routeCodes.superAdmin.administration} />
          <ProtectedRoute component={AdministrationOwners} exact path={routeCodes.superAdmin.administrationOwners} />
          <ProtectedRoute component={AdministrationOwnersCreate} exact path={routeCodes.superAdmin.administrationOwnersCreate} />
          <ProtectedRoute component={AdministrationOwnersShow} exact path={routeCodes.superAdmin.administrationOwnersEdit} />

          <ProtectedRoute component={AdministrationCompanies} exact path={routeCodes.superAdmin.administrationCompany} />
          <ProtectedRoute component={AdministrationCompaniesCreate} exact path={routeCodes.superAdmin.administrationCompanyCreate} />
          <ProtectedRoute component={AdministrationCompaniesShow} exact path={routeCodes.superAdmin.administrationCompanyEdit} />

          <ProtectedRoute component={AdministrationRestaurants} exact path={routeCodes.superAdmin.administrationRestaurant} />
          <ProtectedRoute component={AdministrationRestaurantsCreate} exact path={routeCodes.superAdmin.administrationRestaurantCreate} />
          <ProtectedRoute component={AdministrationRestaurantsShow} exact path={routeCodes.superAdmin.administrationRestaurantEdit} />
          <ProtectedRoute component={Ipratico} exact path={routeCodes.superAdmin.ipratico} />

          <ProtectedRoute
            component={SuperAdminHome}
            exact
            path={routeCodes.home}
          />
          <ProtectedRoute
            component={SuperAdminUserStatistics}
            exact
            path={routeCodes.userStatistic}
          />
          <ProtectedRoute
            component={SuperAdminRestaurants}
            exact
            path={routeCodes.restaurants}
          />
          <ProtectedRoute
            component={SuperAdminBonusesRestaurants}
            exact
            path={routeCodes.bonusesRestaurants}
          />
          <ProtectedRoute
            component={StatisticsPage}
            exact
            path={routeCodes.superAdmin.statistics}
          />

          <ProtectedRoute
            component={TeamPage}
            exact
            path={routeCodes.superAdmin.team}
          />
          <ProtectedRoute
            component={TeamCreatePage}
            exact
            path={routeCodes.superAdmin.teamCreate}
          />
          <ProtectedRoute
            component={TeamEditPage}
            exact
            path={routeCodes.superAdmin.teamShow}
          />

          {/*Menu start*/}
          <ProtectedRoute
            component={MenuPage}
            exact
            path={routeCodes.superAdmin.menu}
          />
          <ProtectedRoute
            component={MenuOptionsCreate}
            exact
            path={routeCodes.superAdmin.menuOptionsCreate}
          />
          <ProtectedRoute
            component={MenuCategoriesCreate}
            exact
            path={routeCodes.superAdmin.menuCategoriesCreate}
          />
          <ProtectedRoute
            component={MenuCategoriesEdit}
            exact
            path={routeCodes.superAdmin.menuCategoriesEdit}
          />
          <ProtectedRoute
            component={MenuOptionsEdit}
            exact
            path={routeCodes.superAdmin.menuOptionsEdit}
          />

          <ProtectedRoute
            component={MenuCategories}
            exact
            path={routeCodes.superAdmin.menuCategories}
          />

          <ProtectedRoute
            component={MenuDishesCreate}
            exact
            path={routeCodes.superAdmin.menuDishesCreate}
          />
          <ProtectedRoute
            component={MenuDishesEdit}
            exact
            path={routeCodes.superAdmin.menuDishesEdit}
          />

          <ProtectedRoute
            component={MenuSpecialMenuCreate}
            exact
            path={routeCodes.superAdmin.specialMenuCreate}
          />
          <ProtectedRoute
            component={MenuSpecialMenuEdit}
            exact
            path={routeCodes.superAdmin.specialMenuEdit}
          />
          {/*Menu end*/}

          {/* Menu pages start */}
          <ProtectedRoute component={MenuPage} exact path={routeCodes.menu} />
          <ProtectedRoute component={MenuCategories} exact path={routeCodes.menuCategories} />
          <ProtectedRoute component={MenuDishesCreate} exact path={routeCodes.menuDishesCreate} />
          <ProtectedRoute component={MenuDishesEdit} exact path={routeCodes.menuDishesEdit} />
          <ProtectedRoute component={MenuCategoriesCreate} exact path={routeCodes.menuCategoriesCreate} />
          <ProtectedRoute component={MenuCategoriesEdit} exact path={routeCodes.menuCategoriesEdit} />
          <ProtectedRoute component={MenuSpecialMenuCreate} exact path={routeCodes.specialMenuCreate} />
          <ProtectedRoute component={MenuSpecialMenuEdit} exact path={routeCodes.specialMenuEdit} />
          <ProtectedRoute component={MenuOptionsCreate} exact path={routeCodes.menuOptionsCreate} />
          <ProtectedRoute component={MenuOptionsEdit} exact path={routeCodes.menuOptionsEdit} />
          {/* Menu pages end */}

          {/*Settings start*/}
          <ProtectedRoute
            component={SettingsV2Page}
            exact
            path={routeCodes.superAdmin.settings}
          />
          <ProtectedRoute
            component={SettingsV2IpraticoPage}
            exact
            path={routeCodes.superAdmin.settingsIpratico}
          />
          <ProtectedRoute
            component={SettingsV2Delivery}
            exact
            path={routeCodes.superAdmin.settingsConfigsDelivery}
          />
          <ProtectedRoute
            component={SettingsV2ServiceCreate}
            exact
            path={routeCodes.superAdmin.settingsConfigsServicesCreate}
          />
          <ProtectedRoute
            component={SettingsV2ServiceEdit}
            exact
            path={routeCodes.superAdmin.settingsConfigsServicesView}
          />
          {/*Settings end*/}

          {/*Offers start*/}
          <ProtectedRoute
            component={OffersMainPage}
            exact
            path={routeCodes.superAdmin.offers}
          />
          <ProtectedRoute
            component={OffersCreateCalendars}
            exact
            path={routeCodes.superAdmin.offersCreatCalendar}
          />
          <ProtectedRoute
            component={OffersCreatePayment}
            exact
            path={routeCodes.superAdmin.offersCreatPayment}
          />
          <ProtectedRoute
            component={OffersCreateAmount}
            exact
            path={routeCodes.superAdmin.offersCreatBonusAmount}
          />
          <ProtectedRoute
            component={OffersCreateBonusReservation}
            exact
            path={routeCodes.superAdmin.offersCreatBonusReservation}
          />
          {/*Offers end*/}

          <ProtectedRoute component={StripeRefresh} exact path={routeCodes.superAdmin.stripeRefresh} />
          <ProtectedRoute component={StripeSuccess} exact path={routeCodes.superAdmin.stripeSuccess} />
          <ProtectedRoute component={QRMainPage} exact path={routeCodes.superAdmin.qr} />
          <ProtectedRoute component={HelpPage} exact path={routeCodes.superAdmin.help} />
          <ProtectedRoute
            component={Home}
            exact
            path={routeCodes.superAdmin.restaurantHome}
          />
          <ProtectedRoute
            component={SuperAdminRestaurantsShow}
            exact
            path={routeCodes.restaurantsView}
          />
        </Switch>
      </SuperAdminAppShell>
    </>
  );
};

export default SuperAdminRoutes;
